import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';

import { useBillingDelinquency } from '~hooks/billing/useBillingDelinquency';

import { ROLES } from '~constants';
import { BillingDelinquencyTier } from '~constants/billing/delinquency';
import { GlobalState } from '~reducers';
import { LDFlags } from '~types/featureFlags';

export interface PaymentMethodsBannerState {
	/**
	 * The business still has billing accounts without a payment method, and the reader can act on
	 * it (PLAT-403). Drives both the global header copy and the in-page billing banner.
	 */
	isPaymentMethodsBannerEnabled: boolean;
	/**
	 * Whether the global header banner should render — and therefore whether the overlays anchored
	 * below it need their offset. One predicate so the banner and the offsets can never disagree.
	 */
	isSuspensionBannerVisible: boolean;
}

/**
 * Single source of truth for the global billing banner.
 *
 * Decided from LaunchDarkly flags and the session alone — no request. CentsBE keeps
 * `bmPaymentMethodsAssignmentBanner` in sync with NetSuite (a nightly sync, plus removal the moment
 * the last payment method is assigned), so the browser never asks NetSuite anything. The previous
 * treatment fetched a NetSuite-backed status on every app mount, which is what overloaded it.
 *
 * Owner only: the banner sends the reader to the billing page, which no other role can open.
 * `netsuiteBmBillingPage` is required as well, because without it that page is the legacy Maxio view,
 * where a payment method cannot be assigned.
 */
export const usePaymentMethodsBanner = (): PaymentMethodsBannerState => {
	const {
		bmPaymentMethodsAssignmentBanner: isAssignmentFlagOn,
		netsuiteBmBillingPage: isNetSuiteBillingPageEnabled,
	} = useFlags<LDFlags>();
	const isOverdue = useBillingDelinquency() === BillingDelinquencyTier.OVERDUE;
	const roleName = useSelector((state: GlobalState) => state.session.roleName);

	const isPaymentMethodsBannerEnabled = Boolean(
		isAssignmentFlagOn && isNetSuiteBillingPageEnabled && roleName === ROLES.owner,
	);

	return {
		isPaymentMethodsBannerEnabled,
		isSuspensionBannerVisible: isPaymentMethodsBannerEnabled || isOverdue,
	};
};
